import React from "react";
import { BILLING_PLANS, BUYABLE_PLAN_KEYS, BillingPlanKey, getGstBreakdown } from "../../../config/billingPlans";
import type { AccountData } from "../../../hooks/useMyAccountData";
import { StripePaymentForm } from "../StripePaymentForm";

interface TopUpModalProps {
  accountData: AccountData;
  selectedPlan: string;
  setSelectedPlan: (plan: string) => void;
  showPaymentStep: boolean;
  setShowPaymentStep: (v: boolean) => void;
  onClose: () => void;
  handlePaymentSuccess?: (creditsAdded: number) => void;
}

const TopUpModal: React.FC<TopUpModalProps> = ({
  accountData,
  selectedPlan,
  setSelectedPlan,
  showPaymentStep,
  setShowPaymentStep,
  onClose,
  handlePaymentSuccess,
}) => {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setShowPaymentStep(false);
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, setShowPaymentStep]);

  return (
  <div
    className="modal-overlay"
    data-testid="topup-modal-overlay"
    onClick={() => {
      onClose();
      setShowPaymentStep(false);
    }}
  >
    <div
      className="modal-content modal-large"
      data-testid="topup-modal-content"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-header" data-testid="topup-modal-header">
        <div className="header-content">
          <h3>{showPaymentStep ? "Payment Details" : "Top Up Your Account"}</h3>
          <p className="modal-subtitle">
            {showPaymentStep
              ? `Complete payment for ${BILLING_PLANS[selectedPlan as BillingPlanKey]?.name} bundle`
              : "Purchase credit bundles for compliance checks. Prices exclude GST."}
          </p>
        </div>
        <button
          className="close-button"
          data-testid="topup-modal-close-button"
          onClick={() => {
            onClose();
            setShowPaymentStep(false);
          }}
        >
          ✕
        </button>
      </div>
      <div className="modal-body" data-testid="topup-modal-body">
        {showPaymentStep ? (
          <StripePaymentForm
            planKey={selectedPlan as BillingPlanKey}
            customerEmail={accountData.adminEmail || ""}
            customerName={accountData.organisation || ""}
            onSuccess={(creditsAdded) => {
              setShowPaymentStep(false);
              onClose();
              if (handlePaymentSuccess) handlePaymentSuccess(creditsAdded);
            }}
            onCancel={() => setShowPaymentStep(false)}
          />
        ) : (
          <>

            <div className="bundles-section" data-testid="bundles-section">
              <h3 className="bundles-title">Select Your Bundle</h3>
              <div
                className="credit-bundles-grid"
                data-testid="credit-bundles-grid"
              >
                {BUYABLE_PLAN_KEYS.map((key) => {
                  const plan = BILLING_PLANS[key];
                  const isSelected = selectedPlan === key;
                  const priceDisplay = `$${(plan.price / 100).toLocaleString()} AUD`;
                  const perCreditDisplay = `$${(plan.pricePerCredit / 100).toFixed(2)} per credit`;
                  const hasBadge = "badge" in plan && plan.badge;
                  const hasDiscount = plan.discountPercent > 0;

                  return (
                    <div
                      key={key}
                      className={`credit-bundle-card ${hasBadge ? "featured" : ""} ${isSelected ? "selected" : ""}`}
                      data-testid={`${plan.id}-bundle-card`}
                      onClick={() => setSelectedPlan(key)}
                    >
                      {hasBadge && (
                        <div className="bundle-badge">{plan.badge}</div>
                      )}
                      <div className="bundle-header">
                        <h4>{plan.name}</h4>
                        <span className="bundle-credits">
                          {plan.credits.toLocaleString()} Credits
                        </span>
                      </div>
                      <div className="bundle-pricing">
                        <div className="price">{priceDisplay}</div>
                        <div className="per-credit">{perCreditDisplay}</div>
                        {hasDiscount && (
                          <div className="savings">
                            {plan.discountPercent}% Savings
                          </div>
                        )}
                      </div>
                      <ul className="bundle-features">
                        {plan.features.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                      <button
                        className={`bundle-select-btn ${isSelected ? "selected" : ""}`}
                        data-testid={`${plan.id}-bundle-select-button`}
                        onClick={() => setSelectedPlan(key)}
                      >
                        {isSelected ? "✓ Selected" : "Select"}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Billing summary — mirrors the signup flow (GST-exclusive
                  prices with a 10% GST line and a GST-inclusive total). */}
              {(() => {
                const plan = BILLING_PLANS[selectedPlan as BillingPlanKey];
                if (!plan) return null;
                const fmt = (cents: number) =>
                  (cents / 100).toLocaleString("en-AU", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  });
                const { subtotalCents, gstCents, totalIncGstCents } = getGstBreakdown(plan.price);
                const rows: { label: string; value: string; bold?: boolean }[] = [
                  { label: "Selected Package:", value: plan.name },
                  { label: "Credits:", value: `${plan.credits.toLocaleString()} credits` },
                  { label: "Subtotal:", value: `$${fmt(subtotalCents)} AUD` },
                  { label: "GST (10%):", value: `$${fmt(gstCents)} AUD` },
                  { label: "Total (inc. GST):", value: `$${fmt(totalIncGstCents)} AUD`, bold: true },
                ];
                return (
                  <div className="billing-summary-card" data-testid="topup-billing-summary">
                    {rows.map((r) => (
                      <div
                        key={r.label}
                        className="summary-row"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          fontWeight: r.bold ? 700 : 400,
                          color: r.bold ? "#1a1a2e" : "#475569",
                          borderTop: r.bold ? "1px solid #e2e8f0" : "none",
                          fontSize: r.bold ? 16 : 14,
                        }}
                      >
                        <span>{r.label}</span>
                        <span>{r.value}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="bundle-info">
                <p>
                  <strong>About Credits:</strong> 1 credit = 1 compliance check.
                  Credits don't expire within the current subscription
                  anniversary period and can be used for any entity type
                  regardless of complexity.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      {!showPaymentStep && (
        <div className="modal-footer" data-testid="topup-modal-footer">
          <button
            className="action-button secondary"
            data-testid="topup-modal-cancel-button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="action-button primary"
            data-testid="topup-modal-purchase-button"
            onClick={() => setShowPaymentStep(true)}
          >
            Continue to Payment
          </button>
        </div>
      )}
    </div>
  </div>
  );
};

export default TopUpModal;
